import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Typography,
  message,
  Space,
  Divider,
  Row,
  Col,
  Select,
  Radio,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { createMiniMaxAccount } from "../services/api";

const { Title } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

const ManageAIsPage = () => {
  const [data, setData] = useState([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const [accountType, setAccountType] = useState("shared");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(undefined);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/accounts`);
      setData(res.data);
    } catch {
      message.error("Không thể tải danh sách tài khoản AI");
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/account`);
      const filtered = res.data.filter((u) => u.role !== 0);
      setUsers(filtered);
    } catch {
      message.error("Không thể tải danh sách người dùng");
    }
  };

  useEffect(() => {
    fetchData();
    loadUsers();
  }, []);

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Xác nhận xoá",
      content: "Bạn có chắc muốn xoá tài khoản AI này?",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/accounts/${id}`);
          message.success("Xoá thành công");
          fetchData();
        } catch {
          message.error("Lỗi khi xoá");
        }
      },
    });
  };

  const handleAdd = async (values) => {
    try {
      setLoading(true);
      const isShared = accountType === "shared";
      const ownerId = accountType === "shared" ? null : selectedUserId;

      if (ownerId === undefined || isNaN(Number(ownerId))) {
        message.error("Thiếu ownerId hợp lệ");
        return;
      }

      const basePayload = {
        ownerId,
        isShared,
      };

      let sent = false;

      if (values.email?.trim() && values.password?.trim()) {
        const payload = {
          provider: "runway",
          email: values.email.trim(),
          password: values.password.trim(),
          ...basePayload,
        };
        console.log("▶️ Gửi Runway:", payload);
        await axios.post(`${API_URL}/accounts`, payload);
        sent = true;
      }

      if (values.minimax_url?.trim() && values.minimax_token?.trim()) {
        const minimax = await createMiniMaxAccount(
          values.minimax_url.trim(),
          values.minimax_token.trim()
        );
        const displayName = minimax?.jwt?.user?.name || minimax.account;

        const payload = {
          provider: "hailuo",
          accountId: minimax.account,
          name: displayName,
          ...basePayload,
        };
        console.log("▶️ Gửi Hailuo:", payload);
        await axios.post(`${API_URL}/accounts`, payload);
        sent = true;
      }

      if (!sent) {
        message.warning(
          "Vui lòng nhập thông tin Runway hoặc Hailuo để thêm tài khoản."
        );
        return;
      }

      message.success("Thêm tài khoản AI thành công");
      fetchData();
      form.resetFields();
      setIsModalVisible(false);
      setAccountType("shared");
      setSelectedUserId(undefined);
    } catch (err) {
      console.error("❌ Lỗi gửi request:", err);
      message.error(err.response?.data?.message || "Thêm thất bại");
    } finally {
      setLoading(false);
    }
  };

  const filteredData =
    filter === "all" ? data : data.filter((item) => item.provider === filter);

  return (
    <div style={{ padding: 40 }}>
      <Title level={3}>Quản lý tài khoản AI (dùng chung & riêng)</Title>

      <Space style={{ marginBottom: 16 }}>
        <Select
          value={filter}
          onChange={setFilter}
          style={{ width: 200 }}
          placeholder="Lọc theo Provider"
        >
          <Select.Option value="all">Tất cả</Select.Option>
          <Select.Option value="runway">Runway</Select.Option>
          <Select.Option value="hailuo">Hailuo</Select.Option>
        </Select>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Thêm tài khoản AI
        </Button>

        <Button onClick={() => window.history.back()}>← Quay lại</Button>
      </Space>

      <Table
        dataSource={filteredData}
        rowKey="id"
        columns={[
          { title: "ID", dataIndex: "id" },
          { title: "Provider", dataIndex: "provider" },
          { title: "Email", dataIndex: "email" },
          { title: "Account ID", dataIndex: "accountId" },
          { title: "Name", dataIndex: "name" },
          {
            title: "Ngày tạo",
            dataIndex: "createdAt",
            render: (text) =>
              new Date(text).toLocaleString("vi-VN", { hour12: false }),
          },
          {
            title: "Hành động",
            render: (_, record) => (
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={() => handleDelete(record.id)}
              >
                Xoá
              </Button>
            ),
          },
        ]}
      />

      <Modal
        open={isModalVisible}
        title="Thêm tài khoản AI"
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        okText="Lưu"
        confirmLoading={loading}
        width={800}
      >
        <Form layout="vertical" form={form} onFinish={handleAdd}>
          <Form.Item label="Loại tài khoản">
            <Radio.Group
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
            >
              <Radio value="shared">Dùng chung</Radio>
              <Radio value="personal">Dùng riêng</Radio>
            </Radio.Group>
          </Form.Item>

          {accountType === "personal" && (
            <Form.Item
              label="Chọn người dùng"
              required
              validateStatus={!selectedUserId ? "error" : ""}
              help={!selectedUserId && "Vui lòng chọn người dùng"}
            >
              <Select
                placeholder="Chọn người dùng"
                value={selectedUserId}
                onChange={setSelectedUserId}
              >
                {users.map((user) => (
                  <Select.Option key={user.id} value={user.id}>
                    {user.username || user.email}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Row gutter={24}>
            <Col span={12}>
              <Divider>Runway AI</Divider>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: "email", message: "Email không hợp lệ" }]}
              >
                <Input placeholder="Nhập email Runway" />
              </Form.Item>
              <Form.Item name="password" label="Mật khẩu">
                <Input.Password placeholder="Mật khẩu Runway (nếu có)" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Divider>Hailuo AI</Divider>
              <Form.Item name="minimax_url" label="Hailuo AI URL">
                <Input placeholder="https://hailuoai.video/..." />
              </Form.Item>
              <Form.Item name="minimax_token" label="Token Hailuo AI">
                <Input placeholder="Nhập token" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageAIsPage;
